import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertOctagon, AlertTriangle, ArrowDown, ArrowRight, ArrowUp, Boxes, CheckCircle2, Cog, Droplets, Factory, Info,
  PackageSearch, ShieldCheck, Timer, Trophy, UserRound, Wrench,
} from "lucide-react";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { api, formatApiError, formatINR, formatDate } from "@/lib/api";
import Collapse, { CARD } from "@/components/Collapse";
import { usePeriod, RollingPeriod, FinancialYearBar } from "@/components/PeriodControls";

// Two chart colours, checked for colour-blind separation: blue is volume / intake, orange is waste / outward.
const BLUE = "#2563EB";
const ORANGE = "#EA580C";
const LEVEL = {
  red:   { box: "border-red-200 bg-red-50",     text: "text-red-800",   icon: AlertOctagon,  label: "Act now" },
  amber: { box: "border-amber-200 bg-amber-50", text: "text-amber-900", icon: AlertTriangle, label: "Watch" },
  info:  { box: "border-slate-200 bg-slate-50", text: "text-slate-700", icon: Info,          label: "Note" },
};
const MACHINE_STATUS = {
  active:      { chip: "bg-emerald-100 text-emerald-800", label: "Running" },
  maintenance: { chip: "bg-red-100 text-red-800",         label: "In maintenance" },
  idle:        { chip: "bg-amber-100 text-amber-900",     label: "Idle" },
};
const GROUP_LABEL = { raw: "Paper & film", ink: "Ink", varnish: "Varnish", consumable: "Consumable" };

const n0 = (v) => (v == null ? "-" : Number(v).toLocaleString("en-IN"));
const weekLabel = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
const monthLabel = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString("en-IN", { month: "short" });

function Delta({ value, prev, better, unit = "", relative = false }) {
  if (value == null || prev == null || value === prev) return null;
  // relative: show the change as a percentage of the earlier figure, for large numbers such as metres
  const d = relative ? (prev ? Math.round(((value - prev) / prev) * 100) : null) : Math.round((value - prev) * 10) / 10;
  if (d == null || d === 0) return null;
  const good = better === "up" ? d > 0 : d < 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] font-semibold ${good ? "text-emerald-600" : "text-red-600"}`}>
      {d > 0 ? <ArrowUp size={11} /> : <ArrowDown size={11} />}{Math.abs(d)}{relative ? "%" : unit}
    </span>
  );
}

function Kpi({ label, value, unit, sub, delta, tone = "neutral", to }) {
  const ring = tone === "bad" ? "ring-red-300/70" : tone === "warn" ? "ring-amber-300/70" : "ring-white/10";
  const body = (
    <div className={`rounded-xl bg-white/5 p-4 ring-1 backdrop-blur transition hover:bg-white/10 ${ring}`}>
      <div className="text-[11px] uppercase tracking-wider text-slate-300 font-medium">{label}</div>
      <div className="mt-2 flex items-end gap-2">
        <span className="font-mono-num text-3xl font-bold leading-none">{value}</span>
        {unit && <span className="text-sm text-slate-300 mb-0.5">{unit}</span>}
        {delta}
      </div>
      <div className="mt-1.5 text-[11px] text-slate-400">{sub}</div>
    </div>
  );
  return to ? <Link to={to}>{body}</Link> : body;
}

function Empty({ icon: Icon = Info, children }) {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-600">
      <Icon size={18} className="mt-0.5 shrink-0 text-slate-400" /> <div>{children}</div>
    </div>
  );
}

function StockBar({ pct, status }) {
  const w = Math.max(2, Math.min(100, pct ?? 0));
  const c = status === "out" ? "bg-red-500" : status === "low" ? "bg-amber-500" : "bg-emerald-500";
  return <div className="h-1.5 w-24 rounded-full bg-slate-100"><div className={`h-1.5 rounded-full ${c}`} style={{ width: `${w}%` }} /></div>;
}

const tip = { borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 };

export default function OpsDashboard() {
  const period = usePeriod();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setErr("");
    try {
      const { data: d } = await api.get("/dashboard/ops", { params: period.params });
      setData(d);
    } catch (e) {
      setErr(formatApiError(e));
    }
  }, [period.days, period.fy, period.month]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  if (err) {
    return (
      <div className="p-8" data-testid="ops-error">
        <div className="mb-3 text-red-700 text-sm">{err}</div>
        <button onClick={load} className="rounded-full bg-slate-900 px-4 py-1.5 text-sm font-semibold text-white">Retry</button>
      </div>
    );
  }
  if (!data) return <div className="p-8 label-caps">Loading…</div>;

  const { kpi, operators, machines, equipment, inventory: inv, blockers, gaps } = data;
  const cur = kpi.current, prev = kpi.previous;
  const atRisk = inv.out + inv.low;
  const held = blockers.material + blockers.ink + blockers.plate + blockers.die;
  const redAlerts = data.alerts.filter((a) => a.level === "red").length;
  const ranked = operators.rows.filter((r) => r.qualified);
  const maxWaste = Math.max(1, ...operators.rows.map((r) => r.waste_pct || 0));
  const plant = cur.waste_pct;
  const hasWeekly = data.weekly.some((w) => w.run_m > 0);
  const hasStores = !gaps.stores;
  const calBad = equipment.calibration.overdue + equipment.calibration.soon;

  return (
    <div className="bg-slate-50 min-h-screen" data-testid="ops-page">
      {/* ===== Hero ===== */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#0B1220] via-[#101a2e] to-[#1a2a47] text-white">
        <div className="pointer-events-none absolute -right-20 -top-28 h-80 w-80 rounded-full bg-[#FF5A00]/25 blur-3xl" />
        <div className="pointer-events-none absolute -left-24 bottom-0 h-64 w-64 rounded-full bg-blue-500/10 blur-3xl" />
        <div className="relative px-8 pt-7 pb-6">
          <div className="flex flex-wrap items-start gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-[#FF8A4C] font-semibold">Plant and stores</div>
              <h1 className="font-display font-black text-3xl lg:text-4xl tracking-tight mt-1">Production &amp; Operations</h1>
              <p className="mt-2 text-sm text-slate-300 max-w-3xl" data-testid="ops-headline">
                {cur.records > 0 ? <>
                  <b className="text-white">{n0(cur.good_m)} m</b> good output in {data.period.label}
                  <span className="mx-2 text-slate-500">·</span><b className="text-white">{cur.waste_pct}%</b> wastage
                  {data.waste_cost > 0 && <> (about <b className="text-orange-300">{formatINR(data.waste_cost).replace(".00", "")}</b> of material)</>}
                </> : <>No printing output recorded in {data.period.label}</>}
                <span className="mx-2 text-slate-500">·</span>
                {redAlerts > 0 ? <b className="text-rose-300">{redAlerts} item{redAlerts === 1 ? "" : "s"} need action now</b> : <b className="text-emerald-300">nothing needs urgent action</b>}
              </p>
            </div>
            <div className="ml-auto flex flex-col items-end gap-2">
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-widest text-slate-400">Data as of</div>
                <div className="font-display font-bold text-xl leading-tight">{formatDate(data.as_of)}</div>
              </div>
              <RollingPeriod period={period} />
              {data.using_latest_data && <span className="text-[11px] text-sky-200">Your data ends before today, so this shows the latest data.</span>}
            </div>
          </div>

          <div className="mt-5">
            <FinancialYearBar period={period} data={data} note="Stock, equipment and held-up jobs always show today's position." />
          </div>

          <div className="mt-4 grid grid-cols-2 lg:grid-cols-6 gap-3">
            <Kpi label="Good output" value={n0(cur.good_m)} unit="m" to="/reports?tab=op-output"
                 delta={<Delta value={cur.good_m} prev={prev.good_m} better="up" relative />} sub={`${cur.jobs} jobs printed`} />
            <Kpi label="Wastage" value={cur.waste_pct != null ? cur.waste_pct : "-"} unit={cur.waste_pct != null ? "%" : ""} to="/reports?tab=wastage"
                 tone={plant != null && prev.waste_pct != null && plant - prev.waste_pct >= 1 ? "bad" : "neutral"}
                 delta={<Delta value={cur.waste_pct} prev={prev.waste_pct} better="down" unit=" pts" />} sub={`${n0(cur.waste_m)} m wasted`} />
            <Kpi label="Machine downtime" value={cur.downtime_h || "-"} unit={cur.downtime_h ? "h" : ""}
                 delta={<Delta value={cur.downtime_h} prev={prev.downtime_h} better="down" unit=" h" />}
                 sub={machines.downtime[0] ? `mostly ${machines.downtime[0].reason}` : "from production logs"} />
            <Kpi label="Stock at risk" value={atRisk} tone={inv.out ? "bad" : atRisk ? "warn" : "neutral"} to="/inventory"
                 sub={`${inv.out} out · ${inv.low} below reorder`} />
            <Kpi label="Jobs held up" value={held} tone={blockers.material ? "bad" : held ? "warn" : "neutral"} to="/daily"
                 sub={`of ${blockers.open_jobs} open jobs`} />
            <Kpi label="Calibration due" value={calBad} tone={equipment.calibration.overdue ? "bad" : calBad ? "warn" : "neutral"} to="/equipment"
                 sub={`${equipment.calibration.overdue} overdue · ${equipment.calibration.soon} within ${equipment.calibration.horizon_days} d`} />
          </div>
        </div>
      </section>

      <div className="px-8 py-6 space-y-6">
        {/* ===== Needs attention ===== */}
        <Collapse id="ops-alerts" testId="ops-alerts" defaultOpen title="Needs your attention"
                  icon={<AlertTriangle size={18} className={redAlerts ? "text-red-600" : "text-slate-400"} />}
                  summary={data.alerts.length ? `${data.alerts.length} item${data.alerts.length === 1 ? "" : "s"}` : "All clear"}>
          {data.alerts.length === 0 ? (
            <div className="flex items-center gap-2 text-sm text-emerald-700"><CheckCircle2 size={18} /> Nothing is out of stock, overdue for calibration or wasting above the plant average.</div>
          ) : (
            <div className="grid gap-2">
              {data.alerts.map((a, i) => {
                const L = LEVEL[a.level], Icon = L.icon;
                return (
                  <Link key={i} to={a.to} className={`flex items-center gap-3 rounded-lg border p-3 transition hover:shadow-sm ${L.box}`}>
                    <Icon size={18} className={L.text} />
                    <span className={`w-14 shrink-0 text-[10px] font-bold uppercase tracking-wide ${L.text}`}>{L.label}</span>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-slate-900">{a.title}</div>
                      {a.detail && <div className="truncate text-xs text-slate-600">{a.detail}</div>}
                    </div>
                    <ArrowRight size={15} className="text-slate-400" />
                  </Link>
                );
              })}
            </div>
          )}
        </Collapse>

        {/* ===== Operators ===== */}
        <Collapse id="ops-operators" testId="ops-operators" defaultOpen title="Operators"
                  icon={<UserRound size={18} className="text-[#FF5A00]" />}
                  summary={ranked.length ? `${ranked.length} ranked · best ${operators.best[0]} · plant wastage ${plant}%` : "No operator data yet"}>
          {operators.rows.length === 0 ? (
            <Empty icon={UserRound}>
              No printing runs with an operator in {data.period.label}. Operators are read from the <b>Operator name</b> on each Printing Log
              and from production logs, so this fills in as soon as those are entered.
            </Empty>
          ) : (
            <>
              <div className="grid gap-3 md:grid-cols-2">
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                  <div className="flex items-center gap-2 text-sm font-bold text-emerald-900"><Trophy size={16} /> Best performers</div>
                  <div className="mt-2 grid gap-1.5">
                    {operators.best.length === 0 && <span className="text-xs text-slate-600">Nobody has {operators.min_records}+ runs in this period yet.</span>}
                    {operators.best.map((name, i) => {
                      const r = operators.rows.find((x) => x.name === name);
                      return (
                        <div key={name} className="flex items-baseline justify-between text-sm">
                          <span className="font-semibold text-slate-900">{i + 1}. {name}</span>
                          <span className="font-mono-num text-emerald-800">{r.waste_pct}% waste · {n0(r.run_m)} m</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                  <div className="flex items-center gap-2 text-sm font-bold text-red-900"><AlertTriangle size={16} /> Most wastage above plant average</div>
                  <div className="mt-2 grid gap-1.5">
                    {operators.worst.length === 0 && <span className="text-xs text-slate-600">Nobody is above the plant average.</span>}
                    {operators.worst.map((name, i) => {
                      const r = operators.rows.find((x) => x.name === name);
                      return (
                        <div key={name} className="flex items-baseline justify-between text-sm">
                          <span className="font-semibold text-slate-900">{i + 1}. {name}</span>
                          <span className="font-mono-num text-red-800">
                            {r.waste_pct}% · {n0(r.excess_waste_m)} m extra{r.excess_cost ? ` (about ${formatINR(r.excess_cost).replace(".00", "")})` : ""}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                      <th className="py-2 pr-3 font-medium">Operator</th>
                      <th className="py-2 pr-3 font-medium text-right">Runs</th>
                      <th className="py-2 pr-3 font-medium text-right">Metres run</th>
                      <th className="py-2 pr-3 font-medium w-48">Wastage</th>
                      <th className="py-2 pr-3 font-medium text-right">Against plant</th>
                      <th className="py-2 pr-3 font-medium text-right">Last period</th>
                      <th className="py-2 font-medium text-right">Extra waste</th>
                    </tr>
                  </thead>
                  <tbody>
                    {operators.rows.map((r) => (
                      <tr key={r.name} className="border-t border-slate-100" data-testid="ops-operator-row">
                        <td className="py-2.5 pr-3 font-semibold text-slate-900">
                          {r.name}
                          {!r.qualified && <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-500">under {operators.min_records} runs</span>}
                          {r.flag && <span className="ml-2 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold text-red-700">Coach</span>}
                        </td>
                        <td className="py-2.5 pr-3 text-right font-mono-num">{r.records}</td>
                        <td className="py-2.5 pr-3 text-right font-mono-num">{n0(r.run_m)}</td>
                        <td className="py-2.5 pr-3">
                          <div className="flex items-center gap-2">
                            <div className="relative h-1.5 w-28 rounded-full bg-slate-100">
                              <div className={`h-1.5 rounded-full ${r.vs_plant > 0 ? "bg-orange-500" : "bg-blue-500"}`} style={{ width: `${Math.max(3, (100 * r.waste_pct) / maxWaste)}%` }} />
                              {plant != null && <div className="absolute -top-0.5 h-2.5 w-px bg-slate-900" style={{ left: `${(100 * plant) / maxWaste}%` }} title={`Plant average ${plant}%`} />}
                            </div>
                            <span className="font-mono-num font-semibold">{r.waste_pct}%</span>
                          </div>
                        </td>
                        <td className={`py-2.5 pr-3 text-right font-mono-num text-xs font-semibold ${r.vs_plant > 0 ? "text-red-600" : "text-emerald-600"}`}>
                          {r.vs_plant != null ? `${r.vs_plant > 0 ? "Worse " : r.vs_plant < 0 ? "Better " : ""}${Math.abs(r.vs_plant)} pts` : "-"}
                        </td>
                        <td className="py-2.5 pr-3 text-right font-mono-num text-xs text-slate-500">
                          {r.prev_waste_pct != null ? <>{r.prev_waste_pct}% <Delta value={r.waste_pct} prev={r.prev_waste_pct} better="down" /></> : "-"}
                        </td>
                        <td className="py-2.5 text-right font-mono-num text-xs">{r.qualified && r.excess_waste_m ? `${n0(r.excess_waste_m)} m` : "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-3 text-xs text-slate-500">
                Wastage is (metres run - good metres) / metres run. The black tick marks the plant average ({plant}%). Operators with fewer than {operators.min_records} runs are shown but not ranked.
                {operators.unnamed_records > 0 && <> {operators.unnamed_records} run{operators.unnamed_records === 1 ? " has" : "s have"} no operator name and {operators.unnamed_records === 1 ? "is" : "are"} counted in the plant total only.</>}
              </p>
            </>
          )}
        </Collapse>

        {/* ===== Machines ===== */}
        <Collapse id="ops-machines" testId="ops-machines" defaultOpen title="Machines"
                  icon={<Cog size={18} className="text-[#FF5A00]" />}
                  summary={machines.rows.length ? `${machines.status.active} running · ${machines.status.maintenance} in maintenance · ${machines.downtime_h} h downtime` : "No machines set up"}>
          {machines.rows.length === 0 ? (
            <Empty icon={Cog}>No machines on the Machines tab yet. Add them there, then log production against each machine to see output, downtime and OEE here.</Empty>
          ) : (
            <div className="grid gap-4 lg:grid-cols-3">
              <div className="lg:col-span-2 grid gap-3 sm:grid-cols-2">
                {machines.rows.map((m) => {
                  const S = MACHINE_STATUS[m.status] || MACHINE_STATUS.active;
                  return (
                    <div key={m.id} className={`${CARD} p-4`} data-testid="ops-machine-card">
                      <div className="flex items-start gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="truncate font-semibold text-slate-900">{m.name}</div>
                          <div className="text-xs text-slate-500">{[m.code, m.type].filter(Boolean).join(" · ") || "Machine"}</div>
                        </div>
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${S.chip}`}>{S.label}</span>
                      </div>
                      {m.hours > 0 || m.good_m > 0 ? (
                        <div className="mt-3 grid grid-cols-4 gap-2 text-center">
                          {[["OEE", m.oee != null ? `${m.oee}%` : "-"], ["Uptime", m.availability != null ? `${m.availability}%` : "-"],
                            ["Waste", m.waste_pct != null ? `${m.waste_pct}%` : "-"], ["Down", `${m.downtime_h} h`]].map(([k, v]) => (
                            <div key={k}><div className="font-mono-num text-base font-bold text-slate-900">{v}</div><div className="text-[10px] uppercase tracking-wide text-slate-400">{k}</div></div>
                          ))}
                        </div>
                      ) : (
                        <div className="mt-3 text-xs text-slate-500">
                          {m.idle_days != null ? `Nothing logged for ${m.idle_days} days` : "No production logged yet"}
                        </div>
                      )}
                      {m.good_m > 0 && <div className="mt-2 text-xs text-slate-500">{n0(m.good_m)} m good · {m.jobs} job{m.jobs === 1 ? "" : "s"}</div>}
                    </div>
                  );
                })}
              </div>
              <div className={`${CARD} p-4`}>
                <div className="flex items-center gap-2 font-semibold text-slate-900"><Timer size={16} className="text-[#FF5A00]" /> Where the time goes</div>
                {machines.downtime.length === 0 ? (
                  <p className="mt-3 text-xs text-slate-500">No downtime recorded in this period. Enter downtime minutes and a reason on production logs to see it here.</p>
                ) : (
                  <div className="mt-3 grid gap-2.5">
                    {machines.downtime.map((d) => (
                      <div key={d.reason}>
                        <div className="flex justify-between text-xs"><span className="text-slate-700">{d.reason}</span><span className="font-mono-num font-semibold">{d.hours} h</span></div>
                        <div className="mt-1 h-1.5 rounded-full bg-slate-100"><div className="h-1.5 rounded-full" style={{ width: `${d.pct}%`, background: ORANGE }} /></div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </Collapse>

        {/* ===== Stock ===== */}
        <Collapse id="ops-stock" testId="ops-stock" defaultOpen title="Paper, ink, varnish and stores"
                  icon={<Boxes size={18} className="text-[#FF5A00]" />}
                  summary={inv.items ? `${inv.out} out · ${inv.low} below reorder · ${inv.items} items` : "No stock items"}>
          {inv.items === 0 ? (
            <Empty icon={Boxes}>No materials in the Inventory tab yet.</Empty>
          ) : (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {inv.groups.map((g) => (
                  <div key={g.group} className="rounded-lg border border-slate-200 p-3">
                    <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-slate-500 font-medium">
                      {g.group === "ink" || g.group === "varnish" ? <Droplets size={13} /> : <Boxes size={13} />} {g.label}
                    </div>
                    <div className="mt-1 flex items-baseline gap-2">
                      <span className="font-mono-num text-2xl font-bold text-slate-900">{g.items}</span>
                      <span className="text-xs text-slate-500">items</span>
                    </div>
                    <div className="mt-1 text-xs">
                      {g.out + g.low === 0 ? <span className="text-emerald-700">All above reorder level</span>
                        : <span className={g.out ? "font-semibold text-red-700" : "font-semibold text-amber-800"}>{g.out ? `${g.out} out · ` : ""}{g.low} low</span>}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-5 flex items-center gap-2 font-semibold text-slate-900"><PackageSearch size={16} className="text-[#FF5A00]" /> Reorder now</div>
              {inv.need_action.length === 0 ? (
                <p className="mt-2 text-sm text-emerald-700">Nothing is at or below its reorder level.</p>
              ) : (
                <div className="mt-2 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                        <th className="py-2 pr-3 font-medium">Item</th><th className="py-2 pr-3 font-medium">Type</th>
                        <th className="py-2 pr-3 font-medium text-right">In stock</th><th className="py-2 pr-3 font-medium">Against reorder level</th>
                        <th className="py-2 pr-3 font-medium text-right">Days left</th><th className="py-2 pr-3 font-medium text-right">Open jobs</th>
                        <th className="py-2 font-medium">Supplier</th>
                      </tr>
                    </thead>
                    <tbody>
                      {inv.need_action.map((r) => (
                        <tr key={r.sku || r.name} className="border-t border-slate-100" data-testid="ops-reorder-row">
                          <td className="py-2.5 pr-3 font-semibold text-slate-900">{r.name}</td>
                          <td className="py-2.5 pr-3 text-xs text-slate-500">{GROUP_LABEL[r.group]}</td>
                          <td className="py-2.5 pr-3 text-right font-mono-num">{n0(r.stock)} {r.unit}</td>
                          <td className="py-2.5 pr-3">
                            <div className="flex items-center gap-2">
                              <StockBar pct={r.pct_of_floor} status={r.status} />
                              <span className={`text-xs font-bold ${r.status === "out" ? "text-red-700" : "text-amber-800"}`}>{r.status === "out" ? "Out of stock" : `${r.pct_of_floor ?? 0}%`}</span>
                            </div>
                          </td>
                          <td className="py-2.5 pr-3 text-right font-mono-num">{r.cover_days != null ? `${r.cover_days} d` : "-"}</td>
                          <td className="py-2.5 pr-3 text-right font-mono-num">{r.open_jobs || "-"}</td>
                          <td className="py-2.5 text-xs text-slate-500">{r.supplier || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {gaps.min_levels && <p className="mt-3 text-xs text-amber-800">No reorder levels are set on any material, so nothing can be flagged as low. Set a reorder level on each item in Inventory.</p>}
              {!gaps.min_levels && inv.need_action.some((r) => r.cover_days == null) && (
                <p className="mt-3 text-xs text-slate-500">Days left needs stock issues recorded in the last 30 days (Stores I/O or stock movements); "-" means no usage was recorded.</p>
              )}

              <div className="mt-5 flex items-center gap-2 font-semibold text-slate-900"><Droplets size={16} className="text-[#FF5A00]" /> Ink and varnish levels</div>
              {inv.inks.length === 0 ? (
                <p className="mt-2 text-sm text-slate-500">No ink or varnish items yet. Add them under Inventory, Ink and Varnish, with a reorder level, to track them here.</p>
              ) : (
                <div className="mt-2 grid gap-x-8 gap-y-2.5 md:grid-cols-2">
                  {inv.inks.map((r) => (
                    <div key={r.sku || r.name} className="flex items-center gap-3">
                      <div className="w-40 truncate text-sm text-slate-800" title={r.name}>{r.name}</div>
                      <StockBar pct={r.pct_of_floor ?? 100} status={r.status} />
                      <div className="font-mono-num text-xs text-slate-600">{n0(r.stock)} {r.unit}</div>
                      {r.status !== "ok" && <span className={`text-[10px] font-bold ${r.status === "out" ? "text-red-700" : "text-amber-800"}`}>{r.status === "out" ? "OUT" : "LOW"}</span>}
                      {r.cover_days != null && <span className="text-[11px] text-slate-400">{r.cover_days} d left</span>}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </Collapse>

        {/* ===== Held-up jobs ===== */}
        <Collapse id="ops-held" testId="ops-held" defaultOpen={held > 0} title="Jobs held up before printing"
                  icon={<Factory size={18} className={held ? "text-amber-600" : "text-slate-400"} />}
                  summary={held ? `${blockers.material} material · ${blockers.ink} ink · ${blockers.plate} plate · ${blockers.die} die` : "None held up"}>
          {blockers.jobs.length === 0 ? (
            <p className="text-sm text-emerald-700">No open job is waiting on material, ink, plate or die.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                    <th className="py-2 pr-3 font-medium">Job</th><th className="py-2 pr-3 font-medium">Customer</th>
                    <th className="py-2 pr-3 font-medium">Waiting on</th><th className="py-2 font-medium text-right">Due</th>
                  </tr>
                </thead>
                <tbody>
                  {blockers.jobs.map((j) => (
                    <tr key={j.mpwo} className="border-t border-slate-100">
                      <td className="py-2.5 pr-3 font-mono-num font-semibold text-slate-900">{j.mpwo}</td>
                      <td className="py-2.5 pr-3 text-slate-700">{j.customer || "-"}</td>
                      <td className="py-2.5 pr-3">{j.waiting_on.map((w) => <span key={w} className="mr-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold capitalize text-amber-900">{w}</span>)}</td>
                      <td className={`py-2.5 text-right text-xs font-semibold ${j.days_to_due != null && j.days_to_due < 0 ? "text-red-600" : "text-slate-600"}`}>
                        {j.days_to_due == null ? "-" : j.days_to_due < 0 ? `${-j.days_to_due} d late` : j.days_to_due === 0 ? "Today" : `in ${j.days_to_due} d`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Collapse>

        {/* ===== Trends ===== */}
        <Collapse id="ops-trend" testId="ops-trend" title="Weekly output and wastage"
                  icon={<Factory size={18} className="text-[#FF5A00]" />}
                  summary={hasWeekly ? "Last 12 weeks" : "No printing output yet"}>
          {!hasWeekly ? <Empty icon={Factory}>No printing output has been recorded yet, so there is no trend to draw.</Empty> : (
            <div className="grid gap-6 lg:grid-cols-2">
              <div>
                <div className="mb-2 text-sm font-semibold text-slate-900">Good metres per week</div>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={data.weekly} margin={{ left: 0, right: 8, top: 4 }}>
                    <CartesianGrid stroke="#eef2f7" vertical={false} />
                    <XAxis dataKey="week" tickFormatter={weekLabel} tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => (v >= 100000 ? `${v / 100000}L` : v >= 1000 ? `${v / 1000}k` : v)} width={42} />
                    <Tooltip labelFormatter={(l) => `Week of ${weekLabel(l)}`} formatter={(v) => [`${n0(v)} m`, "Good output"]} contentStyle={tip} />
                    <Bar dataKey="good_m" fill={BLUE} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div>
                <div className="mb-2 text-sm font-semibold text-slate-900">Wastage % per week</div>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={data.weekly} margin={{ left: 0, right: 8, top: 4 }}>
                    <CartesianGrid stroke="#eef2f7" vertical={false} />
                    <XAxis dataKey="week" tickFormatter={weekLabel} tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} unit="%" width={42} domain={[0, "auto"]} />
                    <Tooltip labelFormatter={(l) => `Week of ${weekLabel(l)}`} formatter={(v) => [`${v}%`, "Wastage"]} contentStyle={tip} />
                    <Line type="monotone" dataKey="waste_pct" stroke={ORANGE} strokeWidth={2} dot={{ r: 3 }} connectNulls={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="lg:col-span-2 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
                {data.stage_waste.map((s) => (
                  <div key={s.stage} className="rounded-lg border border-slate-200 p-3">
                    <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">{s.label} waste</div>
                    <div className="mt-1 font-mono-num text-2xl font-bold text-slate-900">{s.waste_pct != null ? `${s.waste_pct}%` : "-"}</div>
                    <div className="text-xs text-slate-500">{s.records} record{s.records === 1 ? "" : "s"}</div>
                  </div>
                ))}
                {data.shifts.map((s) => (
                  <div key={s.shift} className="rounded-lg border border-slate-200 p-3">
                    <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Shift {s.shift} waste</div>
                    <div className="mt-1 font-mono-num text-2xl font-bold text-slate-900">{s.waste_pct != null ? `${s.waste_pct}%` : "-"}</div>
                    <div className="text-xs text-slate-500">{s.records} run{s.records === 1 ? "" : "s"}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Collapse>

        {/* ===== Stores intake and purchasing ===== */}
        <Collapse id="ops-intake" testId="ops-intake" title="Stores intake and purchasing"
                  icon={<PackageSearch size={18} className="text-[#FF5A00]" />}
                  summary={data.purchasing.top_suppliers[0] ? `Top supplier ${data.purchasing.top_suppliers[0].name}` : "No purchases recorded"}>
          <div className="grid gap-6 lg:grid-cols-2">
            <div>
              <div className="mb-2 text-sm font-semibold text-slate-900">Stores inward and outward, metres per week</div>
              {!hasStores ? <Empty icon={PackageSearch}>No Stores I/O entries in the last 12 weeks.</Empty> : (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={data.stores} margin={{ left: 0, right: 8, top: 4 }}>
                    <CartesianGrid stroke="#eef2f7" vertical={false} />
                    <XAxis dataKey="week" tickFormatter={weekLabel} tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} width={42} tickFormatter={(v) => (v >= 1000 ? `${v / 1000}k` : v)} />
                    <Tooltip labelFormatter={(l) => `Week of ${weekLabel(l)}`} formatter={(v, k) => [`${n0(v)} m`, k]} contentStyle={tip} />
                    <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="inward_m" name="Inward" fill={BLUE} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="outward_m" name="Outward" fill={ORANGE} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
            <div>
              <div className="mb-2 text-sm font-semibold text-slate-900">Purchases per month (before tax)</div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={data.purchasing.monthly} margin={{ left: 0, right: 8, top: 4 }}>
                  <CartesianGrid stroke="#eef2f7" vertical={false} />
                  <XAxis dataKey="month" tickFormatter={monthLabel} tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} width={48} tickFormatter={(v) => (v >= 10000000 ? `${(v / 10000000).toFixed(1)}Cr` : v >= 100000 ? `${(v / 100000).toFixed(0)}L` : v)} />
                  <Tooltip labelFormatter={(l) => monthLabel(l)} formatter={(v) => [formatINR(v).replace(".00", ""), "Purchases"]} contentStyle={tip} />
                  <Bar dataKey="spend" fill={BLUE} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
              <div className="mt-1 text-[11px] text-slate-500">Latest invoice {formatDate(data.purchasing.as_of)}. Includes every purchase invoice, not only materials.</div>
            </div>
          </div>
          {data.purchasing.top_suppliers.length > 0 && (
            <div className="mt-5">
              <div className="mb-2 text-sm font-semibold text-slate-900">Top suppliers, {data.purchasing.suppliers_label}</div>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {data.purchasing.top_suppliers.map((s) => (
                  <div key={s.name} className="flex items-baseline justify-between rounded-lg border border-slate-200 px-3 py-2">
                    <span className="truncate pr-2 text-sm text-slate-800">{s.name}</span>
                    <span className="font-mono-num text-sm font-semibold">{formatINR(s.spend).replace(".00", "")}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Collapse>

        {/* ===== Equipment ===== */}
        <Collapse id="ops-equipment" testId="ops-equipment" defaultOpen={equipment.calibration.overdue > 0} title="Equipment and calibration"
                  icon={<Wrench size={18} className={equipment.calibration.overdue ? "text-red-600" : "text-[#FF5A00]"} />}
                  summary={equipment.total ? `${equipment.active} active · ${equipment.calibration.overdue} overdue · ${equipment.calibration.soon} due soon` : "No equipment listed"}>
          {equipment.total === 0 ? <Empty icon={Wrench}>No equipment on the Equipment tab yet. Add presses, slitters, dies and aniloxes with their next calibration date.</Empty> : (
            <>
              <div className="flex flex-wrap gap-2">
                {equipment.by_type.map((t) => <span key={t.type} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">{t.type} <b className="font-mono-num">{t.count}</b></span>)}
              </div>
              {equipment.attention.length === 0 ? (
                <p className="mt-4 flex items-center gap-2 text-sm text-emerald-700"><ShieldCheck size={16} /> No calibration is overdue or due within {equipment.calibration.horizon_days} days.</p>
              ) : (
                <div className="mt-4 grid gap-2">
                  {equipment.attention.map((a, i) => (
                    <div key={i} className="flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2">
                      <Wrench size={15} className={a.days_left < 0 ? "text-red-600" : "text-amber-600"} />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-semibold text-slate-900">{a.name}</div>
                        <div className="text-xs text-slate-500">{[a.type, a.code, a.location].filter(Boolean).join(" · ")}</div>
                      </div>
                      <span className="text-xs text-slate-500">{formatDate(a.next_calibration)}</span>
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${a.days_left < 0 ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-900"}`}>
                        {a.days_left < 0 ? `${-a.days_left} d overdue` : `in ${a.days_left} d`}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {equipment.calibration.not_set > 0 && <p className="mt-3 text-xs text-slate-500">{equipment.calibration.not_set} active item{equipment.calibration.not_set === 1 ? " has" : "s have"} no next calibration date, so they cannot be tracked.</p>}
            </>
          )}
        </Collapse>
      </div>
    </div>
  );
}
